import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent as _DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@material-ui/core';
import { createElement, FC, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import ProductPricing from '../../../../entity/ProductPricing';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';

export interface Props {
  currentPointBalance: number | null;
  pointTotal: () => number | null;
  purchaseAmount: number;
  open: boolean;
  onRequestClose: () => void;
  onSubmit: () => void;
  className?: string;
  productPricing: ProductPricing | null;
}

export const AdditionalPurchasePriceDialog: FC<Props> = ({
  currentPointBalance,
  pointTotal,
  purchaseAmount,
  open,
  onRequestClose,
  onSubmit,
  className,
  productPricing,
}) => {
  let depositsTotal = 0;
  let subtotal = 0;
  let taxSubtotal = 0;
  let depositsUsage = 0;
  let depositsSubtotal = 0;
  let orderTotal = 0;

  // 必要point数の計算
  const totalUsePoint = !pointTotal() ? 0 : pointTotal()!;

  if (productPricing) {
    if (productPricing.taxRate > 0) {
      depositsTotal = currentPointBalance
        ? currentPointBalance * productPricing.flatRate * (1 + productPricing.taxRate)
        : 0;
    } else {
      depositsTotal = currentPointBalance ? currentPointBalance * productPricing.flatRate : 0;
    }
    subtotal = totalUsePoint * productPricing.flatRate;
    taxSubtotal = subtotal * productPricing.taxRate;
    depositsUsage = subtotal + taxSubtotal - depositsTotal;
    depositsSubtotal = depositsUsage <= 0 ? subtotal + taxSubtotal : depositsTotal;
    orderTotal = depositsUsage <= 0 ? 0 : depositsUsage;
  }

  // 確認画面に表示する用の消費point: 保有pointと必要pointのうち小さい方
  let consumePoint = 0;
  if (currentPointBalance != null && currentPointBalance <= totalUsePoint) {
    consumePoint = currentPointBalance;
  } else {
    consumePoint = totalUsePoint;
  }

  return (
    <Dialog open={open} onClose={onRequestClose} className={className}>
      <DialogTitle>
        <FormattedMessage id="show_project_page.proposal_list.purchase_dialog.title" />
      </DialogTitle>

      <DialogContent>
        <PriceGrid container alignItems="center">
          <CellGrid item xs={5}>
            <Typography variant="body1">
              <FormattedMessage id="show_project_page.proposal_list.purchase_dialog.amount" />
            </Typography>
          </CellGrid>
          <CellGrid item xs={4}>
            <Typography variant="body1">{purchaseAmount}</Typography>
          </CellGrid>

          {productPricing ? (
            <>
              <CellGrid item xs={5}>
                <Typography variant="body1">
                  <FormattedMessage id="show_project_page.proposal_list.purchase_dialog.total_price" />
                </Typography>
              </CellGrid>
              <CellGrid item xs={4}>
                <Typography variant="body1">{productPricing.currency + subtotal.toLocaleString()}</Typography>
              </CellGrid>
            </>
          ) : null}

          {productPricing ? (
            <>
              <CellGrid item xs={5}>
                <Typography variant="body1">
                  <FormattedMessage id="show_project_page.proposal_list.purchase_dialog.tax" />
                </Typography>
              </CellGrid>
              <CellGrid item xs={4}>
                <Typography variant="body1">{productPricing.currency + taxSubtotal.toLocaleString()}</Typography>
              </CellGrid>
            </>
          ) : null}

          {productPricing ? (
            <>
              <CellGrid item xs={5}>
                <Typography variant="body1">
                  <FormattedMessage id="show_project_page.proposal_list.purchase_dialog.point" />
                </Typography>
              </CellGrid>
              <CellGrid item container xs={4} direction={'column'}>
                <Grid item>
                  <Typography variant="body1">
                    -{productPricing.currency + depositsSubtotal.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="caption">(-{consumePoint.toLocaleString()}p)</Typography>
                </Grid>
              </CellGrid>
            </>
          ) : null}

          {productPricing ? (
            <>
              <CellGrid item xs={5}>
                <Typography variant="body1">
                  <FormattedMessage id="show_project_page.proposal_list.purchase_dialog.order" />
                </Typography>
              </CellGrid>
              <CellGrid item xs={4}>
                <Typography variant="body1">{productPricing.currency + orderTotal.toLocaleString()}</Typography>
              </CellGrid>
            </>
          ) : null}
        </PriceGrid>

        <DescriptionGrid container item alignContent="flex-end" direction="column">
          {currentPointBalance != null && currentPointBalance > 0 && productPricing ? (
            <Grid item xs={10}>
              <Typography variant="caption">
                <FormattedMessage
                  id="show_project_page.proposal_list.purchase_dialog.point_description"
                  values={{
                    pointBalance: currentPointBalance.toLocaleString(),
                    pointBalancePrice:
                      productPricing.currency +
                      (currentPointBalance * productPricing.flatRate * (1 + productPricing.taxRate)).toLocaleString(),
                  }}
                />
              </Typography>
            </Grid>
          ) : null}
          <Grid item xs={10}>
            <Typography variant="caption">
              <FormattedMessage id="show_project_page.proposal_list.purchase_dialog.notice" />
            </Typography>
          </Grid>
        </DescriptionGrid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onRequestClose} color="default">
          <FormattedMessage id="show_project_page.proposal_list.purchase_dialog.button.cancel" />
        </Button>
        <Button onClick={onSubmit} color="primary" variant="contained">
          <FormattedMessage id="show_project_page.proposal_list.purchase_dialog.button.purchase" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogContent = styled(_DialogContent)({
  maxWidth: '420px',
});

const DescriptionGrid = styled(Grid)({
  marginTop: SPACING_REGULAR,
});

const PriceGrid = styled(Grid)({
  textAlign: 'end',
});

const CellGrid = styled(Grid)({
  marginTop: SPACING_UNIT,
});
